import React from "react";
import Icon from "./Icon";

// Reusable hologram-style hero used across pages.
// bg image on the right with bright fade-to-white on the left, plus floating neon cards.
export const HologramHero = ({ label, title, subtitle, paragraphs = [], badges = [], cards = [], image, topRight }) => {
  return (
    <section className="relative overflow-hidden bg-[#eef2f7]" data-testid="page-hero">
      {/* background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${image})` }}
      />
      {topRight && (
        <div className="absolute top-24 right-5 lg:right-8 z-20">{topRight}</div>
      )}
      <div className="absolute inset-0 hero-fade-left" />
      {/* subtle technical grid */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#0f2747 1px, transparent 1px), linear-gradient(90deg, #0f2747 1px, transparent 1px)",
          backgroundSize: "46px 46px",
        }}
      />

      <div className="relative max-w-[1320px] mx-auto px-5 lg:px-8 pt-32 pb-20 lg:pt-36 lg:pb-28">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up hero-content">
            {label && (
              <p className="text-blue-brand font-bold uppercase tracking-[0.18em] text-sm mb-4">{label}</p>
            )}
            <h1 className="font-head font-extrabold text-ink text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] mb-5">
              {title}
            </h1>
            {subtitle && (
              <p className="text-blue-brand font-semibold text-lg md:text-xl mb-6">{subtitle}</p>
            )}
            <div className="space-y-4 max-w-xl">
              {paragraphs.map((p, i) => (
                <p key={i} className="font-medium text-[1.0625rem] md:text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                  {p}
                </p>
              ))}
            </div>

            {badges.length > 0 && (
              <div className="flex flex-wrap gap-x-8 gap-y-4 mt-9">
                {badges.map((b, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Icon name={b.icon} size={26} style={{ color: b.color }} strokeWidth={1.8} />
                    <span className="font-head font-bold text-ink text-[15px]" style={{ color: b.textColor || "var(--ink)" }}>
                      {b.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* floating cards over the image (desktop) */}
          <div className="relative hidden lg:block min-h-[500px]">
            {cards.map((c, i) => (
              <div
                key={i}
                className="holo-card absolute p-4 w-[210px]"
                style={{ ...c.pos }}
              >
              <div className="flex items-start justify-between mb-2">
                  <span className="font-head font-bold text-[13px] uppercase tracking-wide leading-tight text-[#dbeafe] max-w-[130px]">
                    {c.title}
                  </span>
                  <Icon name={c.icon} size={24} className="text-blue-300 shrink-0" strokeWidth={1.6} />
                </div>
                <div className="space-y-1.5 mt-3">
                  {[0, 1, 2].map((l) => (
                    <div key={l} className="h-1 rounded-full bg-blue-300/50 pulse-line" style={{ width: `${90 - l * 18}%`, animationDelay: `${l * 0.3}s` }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HologramHero;
